import { ArrowDropDown as ArrowDropDownIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";

import {
  CapabilityGrain,
  MatrixColumn,
  MatrixColumns,
  MatrixRank,
  MatrixRows,
  MatrixSort,
} from "../../../types/analytics";
import {
  CAPABILITY_GRAINS,
  MATRIX_RANKS,
  MatrixSettings,
  columnsAdmittedBy,
} from "../analyticsState";
import { columnName } from "./matrixModel";
import { CAPABILITY_PRESETS, CUSTOM, MOST_CLAIMED, presetOf } from "./matrixPresets";

// What the capabilities chip reads as the set in force
export const setLabel = (settings: MatrixSettings): string =>
  settings.capabilitySet === CUSTOM
    ? `Custom · ${settings.capabilities.length}`
    : (presetOf(settings.capabilitySet)?.label ?? "Most claimed");

// How long a pause in typing lasts before the find is written to the address
export const FIND_DEBOUNCE_MS = 300;

const ROW_CHOICES: readonly { value: MatrixRows; label: string; hint: string }[] = [
  { value: "products", label: "Products", hint: "one row each · ● claimed" },
  { value: "categories", label: "Categories", hint: "at a rank · counts" },
  { value: "makers", label: "Makers", hint: "counts" },
  { value: "capabilities", label: "Capabilities", hint: "against conditions or modes" },
];

const COLUMN_LABELS: Record<MatrixColumns, string> = {
  capabilities: "Capabilities",
  conditions: "Conditions",
  modes: "Modes",
};

const COLUMN_HINTS: Record<MatrixColumns, string> = {
  capabilities: "what the products can do",
  conditions: "the conditions their claims state",
  modes: "the modes their claims state",
};

const GRAIN_LABELS: Record<CapabilityGrain, string> = {
  core: "core",
  condition: "+ condition",
  mode: "+ mode",
};

const FIND_PLACEHOLDERS: Record<MatrixRows, string> = {
  products: "Find a product or maker",
  categories: "Find a class",
  makers: "Find a maker",
  capabilities: "Find a capability",
};

const SORT_LABELS = (rows: MatrixRows): Record<MatrixSort, string> => ({
  count: rows === "products" ? "most claims" : "most products",
  name: "name",
});

// A control the height of the design's segments
const CONTROL_HEIGHT = 30;

const CHIP_SX = {
  height: CONTROL_HEIGHT,
  px: 1.25,
  gap: 0.75,
  fontSize: 12.5,
  fontWeight: 400,
  textTransform: "none",
  whiteSpace: "nowrap",
  color: "text.primary",
  borderColor: (theme: Theme) => alpha(theme.palette.chart.ink, 0.1),
  backgroundColor: (theme: Theme) => alpha(theme.palette.chart.ink, 0.03),
  "&:hover": { borderColor: (theme: Theme) => alpha(theme.palette.chart.ink, 0.2) },
} as const;

const SEGMENT_SX = {
  height: CONTROL_HEIGHT,
  px: 1.5,
  textTransform: "none",
  fontSize: 13,
} as const;

// The small word before a chip's value
const CHIP_LABEL_SX = { color: "text.disabled", fontSize: 12.5 } as const;

interface Choice<T extends string | null> {
  value: T;
  label: string;
  hint?: string;
}

interface ChipMenuProps<T extends string | null> {
  // The word before the value, and the menu's accessible name
  label: string;
  // What the chip reads as the value in force
  valueLabel: string;
  value: T;
  choices: readonly Choice<T>[];
  onChoose: (value: T) => void;
}

// A chip that opens a menu of the choices, the one in force marked
function ChipMenu<T extends string | null>({
  label,
  valueLabel,
  value,
  choices,
  onChoose,
}: ChipMenuProps<T>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        aria-haspopup="menu"
        aria-expanded={anchor ? true : undefined}
        aria-label={`${label}: ${valueLabel}`}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={CHIP_SX}
      >
        <Box component="span" sx={CHIP_LABEL_SX}>
          {label}
        </Box>
        {valueLabel}
        <ArrowDropDownIcon sx={{ fontSize: 16, mx: -0.5, color: "text.disabled" }} />
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        MenuListProps={{ "aria-label": label }}
        slotProps={{ paper: { sx: { minWidth: 280, backgroundColor: "background.raised" } } }}
      >
        {choices.map((choice) => (
          <MenuItem
            key={choice.value ?? "none"}
            role="menuitemradio"
            aria-checked={choice.value === value}
            selected={choice.value === value}
            onClick={() => {
              setAnchor(null);
              onChoose(choice.value);
            }}
            sx={{ fontSize: 13, display: "flex", justifyContent: "space-between", gap: 1.5 }}
          >
            {choice.label}
            {choice.hint && (
              <Typography
                component="span"
                color="text.disabled"
                sx={{ fontSize: 11.5, textAlign: "right" }}
              >
                {choice.hint}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

interface MatrixControlsProps {
  settings: MatrixSettings;
  // The columns shown, which the rows can be narrowed to those claiming
  columns: MatrixColumn[];
  // A change of the settings; the caller resets what it invalidates
  onChange: (change: Partial<MatrixSettings>) => void;
}

// The controls of the table: the pair of rows and columns, the grain and the
// rank where they apply, then the narrowing of the rows
const MatrixControls: React.FC<MatrixControlsProps> = ({ settings, columns, onChange }) => {
  const { rows, columns: columnKind, grain, rank, find, claiming, sort } = settings;
  const admitted = columnsAdmittedBy(rows);

  // The find is typed into a draft and written to the address after a pause
  const [draft, setDraft] = useState(find);
  const written = useRef(find);
  useEffect(() => {
    // The address changed elsewhere (a change of rows clears it)
    if (find !== written.current) {
      written.current = find;
      setDraft(find);
    }
  }, [find]);
  useEffect(() => {
    if (draft === written.current) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      written.current = draft;
      onChange({ find: draft });
    }, FIND_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, onChange]);

  const claimed = columns.find((column) => column.id === claiming);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        columnGap: 1.25,
        rowGap: 1,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <ChipMenu
          label="Rows"
          valueLabel={ROW_CHOICES.find((choice) => choice.value === rows)?.label ?? rows}
          value={rows}
          choices={ROW_CHOICES}
          onChoose={(next) => onChange({ rows: next })}
        />
        <Typography component="span" color="text.disabled" aria-hidden sx={{ fontSize: 13 }}>
          ×
        </Typography>
        {admitted.length > 1 ? (
          <ChipMenu
            label="Columns"
            valueLabel={COLUMN_LABELS[columnKind]}
            value={columnKind}
            choices={admitted.map((value) => ({
              value,
              label: COLUMN_LABELS[value],
              hint: COLUMN_HINTS[value],
            }))}
            onChoose={(next) => onChange({ columns: next })}
          />
        ) : (
          // Only one choice: a plain chip, not a menu
          <Box
            component="span"
            aria-label={`Columns: ${COLUMN_LABELS[columnKind]}`}
            role="note"
            sx={{
              ...CHIP_SX,
              display: "inline-flex",
              alignItems: "center",
              boxSizing: "border-box",
              border: 1,
              borderRadius: (theme) => `${theme.radius.surface}px`,
            }}
          >
            <Box component="span" sx={CHIP_LABEL_SX}>
              Columns
            </Box>
            {COLUMN_LABELS[columnKind]}
          </Box>
        )}
        {columnKind === "capabilities" && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={grain}
            aria-label="Grain"
            onChange={(_event, next: CapabilityGrain | null) => {
              if (next) {
                onChange({ grain: next });
              }
            }}
          >
            {CAPABILITY_GRAINS.map((value) => (
              <ToggleButton key={value} value={value} sx={SEGMENT_SX}>
                {GRAIN_LABELS[value]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        <ChipMenu
          label="Capabilities"
          valueLabel={setLabel(settings)}
          value={settings.capabilitySet}
          choices={[
            {
              value: MOST_CLAIMED,
              label: "Most claimed",
              hint: "of the selection, general ones left out",
            },
            ...CAPABILITY_PRESETS.map((preset) => ({
              value: preset.id,
              label: preset.label,
              hint: `${preset.abilities.length} capabilities`,
            })),
            // Custom is the set put together here: listed only while in force
            ...(settings.capabilitySet === CUSTOM
              ? [
                  {
                    value: CUSTOM,
                    label: "Custom",
                    hint: `${settings.capabilities.length} capabilities`,
                  },
                ]
              : []),
          ]}
          onChoose={(next) =>
            onChange({
              capabilitySet: next,
              capabilities: next === CUSTOM ? settings.capabilities : [],
            })
          }
        />
        {rows === "categories" && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={rank}
            aria-label="Rank"
            onChange={(_event, next: MatrixRank | null) => {
              if (next) {
                onChange({ rank: next });
              }
            }}
          >
            {MATRIX_RANKS.map((value) => (
              <ToggleButton key={value} value={value} sx={SEGMENT_SX}>
                {value}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <TextField
          size="small"
          hiddenLabel
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={FIND_PLACEHOLDERS[rows]}
          inputProps={{ "aria-label": FIND_PLACEHOLDERS[rows], maxLength: 100 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 220,
            "& .MuiOutlinedInput-root": {
              height: CONTROL_HEIGHT,
              fontSize: 13,
              backgroundColor: "background.paper",
            },
          }}
        />
        <ChipMenu<string | null>
          label="Claiming"
          valueLabel={claimed ? columnName(claimed) : "any"}
          value={claimed ? claimed.id : null}
          choices={[
            { value: null, label: "any" },
            ...columns.map((column) => ({
              value: column.id,
              label: column.qualifier
                ? `${columnName(column)} · ${column.qualifier.name ?? column.qualifier.id}`
                : columnName(column),
            })),
          ]}
          onChoose={(next) => onChange({ claiming: next })}
        />
        <ChipMenu
          label="Sort"
          valueLabel={SORT_LABELS(rows)[sort]}
          value={sort}
          choices={(["count", "name"] as const).map((value) => ({
            value,
            label: SORT_LABELS(rows)[value],
          }))}
          onChoose={(next) => onChange({ sort: next })}
        />
      </Box>
    </Box>
  );
};

export default MatrixControls;
